import { useCallback, useEffect, useMemo, useState } from 'react';

import { countries } from 'constants/countries';
import { getIdLabel } from 'helpers/ids';
import { useGetIdTypeOptions } from 'hooks/resolve-id-doc-types';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  FormMessage,
  Input,
  InputGroup,
  MessageType,
  MultipleSelect,
  Select,
  Textarea,
  ToggleSwitch,
} from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';

import {
  useCompleteDocumentRequestMutation,
  useGetDocumentRequestsQuery,
  useNotifyClientDocumentRequestMutation,
  useResendDocumentRequestMutation,
  useUpdateDocumentRequestMutation,
} from 'api/document-requests-api';

const DOCUMENT_REQUEST_FORM_DEFAULTS = {
  document_name: '',
  upload_type: 'supporting_doc',
  message: '',
  repeat_request_days: 1,
  max_requests: 5,
};

const clampInt = (raw, fallback, min, max) => {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const requestMatchesModal = (request, checkId, subCheck) => {
  if (request.completed_at) return false;
  const links = Array.isArray(request.links) ? request.links : [];
  if (subCheck) {
    const home =
      Number(request.candidate_check_id) === Number(checkId) &&
      Number(request.sub_check_id) === Number(subCheck.id);
    const linked = links.some(link => Number(link.sub_check_id) === Number(subCheck.id));
    return home || linked;
  }
  const home = Number(request.candidate_check_id) === Number(checkId) && !request.sub_check_id;
  const linked = links.some(
    link => Number(link.candidate_check_id) === Number(checkId) && !link.sub_check_id,
  );
  return home || linked;
};

const SectionHeading = ({ children, className = '' }) => (
  <span className={`text-neutral-7 font-semibold text-[16px] mb-4 block ${className}`.trim()}>
    {children}
  </span>
);

export const DocumentRequestModal = ({
  visible,
  check,
  checkType,
  subCheck = null,
  linkableChecks = [],
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: DOCUMENT_REQUEST_FORM_DEFAULTS,
  });

  const uploadType = watch('upload_type');
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [additionalChecks, setAdditionalChecks] = useState([]);

  // ID document request state
  const [selectedIdType, setSelectedIdType] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Check if ID document is selected
  const isIdDocument = uploadType === 'id_document';

  // Get ID type options
  const { options: rawIdTypeOptions = [] } = useGetIdTypeOptions();
  const idTypeOptions = useMemo(
    () => rawIdTypeOptions.map(o => ({ value: o.value, label: o.name })),
    [rawIdTypeOptions],
  );

  const handleIdTypeChange = useCallback(
    val => setSelectedIdType(rawIdTypeOptions.find(o => o.value === val) || null),
    [rawIdTypeOptions],
  );

  const handleCountryChange = useCallback(val => setSelectedCountry(val || null), []);

  const { data: documentRequests = [], refetch } = useGetDocumentRequestsQuery(
    { checkId: check?.id },
    { skip: !check?.id || !visible },
  );

  const [updateDocumentRequest] = useUpdateDocumentRequestMutation();
  const [resendDocumentRequest] = useResendDocumentRequestMutation();
  const [notifyClientDocumentRequest] = useNotifyClientDocumentRequestMutation();
  const [completeDocumentRequest] = useCompleteDocumentRequestMutation();

  // Filter by sub_check_id if applicable, and exclude completed
  const additionalCheckOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const add = (value, label) => {
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label });
    };
    (linkableChecks || []).forEach(otherCheck => {
      const onThisCheck = otherCheck.id === check?.id;
      const title = otherCheck.title || otherCheck.type;
      if (!onThisCheck) add(String(otherCheck.id), title);
      (otherCheck.sub_checks || []).forEach(otherSubCheck => {
        if (subCheck && otherSubCheck.id === subCheck.id) return;
        add(`${otherCheck.id}:${otherSubCheck.id}`, `${title} — ${otherSubCheck.title}`);
      });
    });
    return options;
  }, [linkableChecks, check?.id, subCheck]);

  const activeRequests = useMemo(() => {
    if (!documentRequests) return [];
    return documentRequests.filter(request => requestMatchesModal(request, check?.id, subCheck));
  }, [documentRequests, subCheck, check?.id]);

  // Refetch when modal opens
  useEffect(() => {
    if (visible && check?.id) {
      refetch();
    }
  }, [visible, check?.id, refetch]);

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      reset(DOCUMENT_REQUEST_FORM_DEFAULTS);
      setSubmitError(null);
      setConfirmAction(null);
      setSelectedIdType(null);
      setSelectedCountry(null);
      setAdditionalChecks([]);
    }
  }, [visible, reset]);

  const onFormSubmit = async data => {
    // Validate ID document request has required fields
    if (isIdDocument && !selectedIdType) {
      setSubmitError(t('admin.candidate.documentRequest.idTypeRequired'));
      return;
    }

    // Validate non-ID document requests have document name
    if (!isIdDocument && !data.document_name?.trim()) {
      setSubmitError(t('admin.candidate.documentRequest.documentNameRequired'));
      return;
    }

    const repeatDays = clampInt(data.repeat_request_days, 1, 1, 30);
    const maxRequests = clampInt(data.max_requests, 5, 1, 50);

    // Submit directly without confirmation for initial request
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // Build upload metadata for ID document requests (server merges id_country into canonical id_type)
      const uploadMetadata = isIdDocument
        ? {
            id_type: selectedIdType?.value || '',
            ...(selectedCountry ? { id_country: selectedCountry } : {}),
          }
        : {};

      // For ID documents, use the translated ID type label as document name
      const documentName = isIdDocument
        ? getIdLabel(selectedIdType?.value, selectedCountry)
        : data.document_name;

      const result = await onSubmit({
        ...data,
        document_name: documentName,
        upload_type: data.upload_type,
        sub_check_id: subCheck?.id,
        upload_metadata: uploadMetadata,
        repeat_request_days: repeatDays,
        initial_request_days: repeatDays,
        max_requests: maxRequests,
        additional_checks: additionalChecks.map(value => {
          const [checkId, subCheckId] = String(value).split(':');
          return {
            candidate_check_id: Number(checkId),
            sub_check_id: subCheckId ? Number(subCheckId) : null,
          };
        }),
      });
      if (result?.success === false) {
        setSubmitError(result.error);
      } else {
        reset(DOCUMENT_REQUEST_FORM_DEFAULTS);
        setSelectedIdType(null);
        setSelectedCountry(null);
        setAdditionalChecks([]);
        refetch();
      }
    } catch (error) {
      console.error('Failed to create document request:', error);
      setSubmitError(error.message || 'Failed to create document request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTogglePause = async (requestId, currentPaused) => {
    try {
      await updateDocumentRequest({
        id: requestId,
        data: { paused: !currentPaused },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to toggle pause:', error);
      setSubmitError(
        error.data?.error?.message || error.message || 'Failed to update document request',
      );
    }
  };

  const handleResendClick = request => {
    setConfirmAction({
      type: 'resend',
      requestId: request.id,
      message: t('admin.candidate.documentRequest.confirmResendToCandidate', {
        documentName: request.document_name,
      }),
    });
  };

  const handleConfirmedResend = async () => {
    const requestId = confirmAction.requestId;
    setConfirmAction(null);
    try {
      await resendDocumentRequest({ id: requestId }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to resend:', error);
      setSubmitError(
        error.data?.error?.message || error.message || 'Failed to resend document request',
      );
    }
  };

  const handleNotifyClientClick = request => {
    setConfirmAction({
      type: 'notifyClient',
      requestId: request.id,
      message: t('admin.candidate.documentRequest.confirmNotifyClient', {
        documentName: request.document_name,
      }),
    });
  };

  const handleCompleteClick = request => {
    setConfirmAction({
      type: 'complete',
      requestId: request.id,
      message: t('admin.candidate.documentRequest.confirmComplete', {
        documentName: request.document_name,
      }),
    });
  };

  const handleConfirmedComplete = async () => {
    const requestId = confirmAction.requestId;
    setConfirmAction(null);
    try {
      await completeDocumentRequest({ id: requestId }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to complete:', error);
      setSubmitError(error.data?.error?.message || error.message || 'Failed to complete request');
    }
  };

  const handleConfirmedNotifyClient = async () => {
    const requestId = confirmAction.requestId;
    setConfirmAction(null);
    try {
      await notifyClientDocumentRequest({ id: requestId }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to notify client:', error);
      setSubmitError(error.data?.error?.message || error.message || 'Failed to notify client');
    }
  };

  const handleConfirm = () => {
    if (confirmAction?.type === 'resend') {
      handleConfirmedResend();
    } else if (confirmAction?.type === 'notifyClient') {
      handleConfirmedNotifyClient();
    } else if (confirmAction?.type === 'complete') {
      handleConfirmedComplete();
    }
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={t('admin.candidate.documentRequest.title')}
      icon="file-text"
      small>
      <div className="d-flex flex-column u-margin-top--large" style={{ minWidth: '400px' }}>
        {/* Confirmation Dialog */}
        {confirmAction && (
          <div
            className="d-flex flex-column u-gap-medium u-padding u-margin-bottom u-border u-border-radius"
            style={{ backgroundColor: 'var(--neutral-02)' }}>
            <p className="u-margin-0">{confirmAction.message}</p>
            <div className="d-flex u-gap-small">
              <Button type="secondary" onClick={() => setConfirmAction(null)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" onClick={handleConfirm}>
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        )}

        {/* Existing Active Requests */}
        {activeRequests.length > 0 && (
          <div className="d-flex flex-column u-margin-bottom--large">
            <SectionHeading>{t('admin.candidate.documentRequest.activeRequests')}</SectionHeading>
            {activeRequests.map(request => (
              <div
                key={request.id}
                className="border border-neutral-4 rounded-2xl p-4 mb-4 bg-neutral-1">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-[12px] font-semibold text-text-secondary">
                    {request.document_name}
                  </span>
                  <div className="d-flex u-gap-small">
                    <IconButton
                      icon="refresh-ccw"
                      tip={t('admin.candidate.documentRequest.resendToCandidate')}
                      onClick={() => handleResendClick(request)}
                    />
                    <IconButton
                      icon="bell"
                      tip={t('admin.candidate.documentRequest.notifyClient')}
                      onClick={() => handleNotifyClientClick(request)}
                    />
                    <IconButton
                      icon="check-circle"
                      tip={t('admin.candidate.documentRequest.markComplete')}
                      onClick={() => handleCompleteClick(request)}
                    />
                  </div>
                </div>
                <div className="d-flex u-gap-medium t-small t-faded u-margin-bottom--small">
                  <span>
                    {t('admin.candidate.documentRequest.emailsSent')}: {request.emails_sent || 0}
                  </span>
                  <span>
                    {t('admin.candidate.documentRequest.createdAt')}:{' '}
                    {moment(request.created_at).format('DD/MM/YYYY')}
                  </span>
                </div>
                <ToggleSwitch
                  label={t('admin.candidate.documentRequest.pauseReminders')}
                  inputProps={{
                    checked: !!request.paused,
                    onChange: () => handleTogglePause(request.id, request.paused),
                  }}
                  status={
                    request.paused
                      ? { text: t('admin.candidate.documentRequest.paused'), status: 'warning' }
                      : { text: t('admin.candidate.documentRequest.active'), status: 'success' }
                  }
                />
              </div>
            ))}
          </div>
        )}

        {/* Create New Request Form */}
        <form onSubmit={handleSubmit(onFormSubmit)}>
          {activeRequests.length > 0 && (
            <>
              <div className="u-divider u-margin-bottom--large" />
              <SectionHeading>{t('admin.candidate.documentRequest.createNew')}</SectionHeading>
            </>
          )}

          {/* Document Type selector first */}
          <InputGroup title={t('admin.candidate.documentRequest.uploadType')}>
            <Select name="upload_type" register={register} value={uploadType}>
              <Select.Item value="supporting_doc">
                {t('admin.candidate.documentRequest.supportingDocument')}
              </Select.Item>
              <Select.Item value="result">
                {t('admin.candidate.documentRequest.resultFile')}
              </Select.Item>
              <Select.Item value="id_document">
                {t('admin.candidate.documentRequest.idDocument')}
              </Select.Item>
            </Select>
            <span className="u-font-size-small secondary-text u-margin-top--small">
              {uploadType === 'result'
                ? t('admin.candidate.documentRequest.resultFileDescription')
                : uploadType === 'id_document'
                  ? t('admin.candidate.documentRequest.idDocumentDescription')
                  : t('admin.candidate.documentRequest.supportingDocumentDescription')}
            </span>
          </InputGroup>

          {/* ID Type and Country selectors - shown when ID Document is selected */}
          {isIdDocument && (
            <div className="d-flex flex-column u-gap">
              <InputGroup title={t('admin.candidate.documentRequest.idType') + ' *'}>
                <Select
                  name="id_type"
                  placeholder={t('admin.candidate.documentRequest.selectIdType')}
                  value={selectedIdType?.value || ''}
                  inputProps={{
                    onChange: e => handleIdTypeChange(e.target.value),
                  }}>
                  {idTypeOptions.map(option => (
                    <Select.Item key={option.value} value={option.value}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>

              <InputGroup title={t('admin.candidate.documentRequest.country')}>
                <Select
                  name="country"
                  placeholder={t('admin.candidate.documentRequest.selectCountry')}
                  value={selectedCountry || ''}
                  inputProps={{
                    onChange: e => handleCountryChange(e.target.value),
                  }}>
                  <Select.Item value="">{t('common.optional')}</Select.Item>
                  {(countries || []).map(({ alpha2, name }) => (
                    <Select.Item key={alpha2} value={alpha2}>
                      {name}
                    </Select.Item>
                  ))}
                </Select>
                <span className="u-font-size-small secondary-text u-margin-top--small">
                  {t('admin.candidate.documentRequest.countryDescription')}
                </span>
              </InputGroup>
            </div>
          )}

          {/* Document Name - hidden for ID documents (auto-populated from ID type) */}
          {!isIdDocument && (
            <InputGroup title={t('admin.candidate.documentRequest.documentName') + ' *'}>
              <Input
                register={register}
                name="document_name"
                validators={{ required: !isIdDocument }}
                error={errors?.document_name?.type}
                placeholder={t('admin.candidate.documentRequest.documentNamePlaceholder')}
              />
            </InputGroup>
          )}

          <InputGroup title={t('admin.candidate.documentRequest.message')}>
            <Textarea
              register={register}
              name="message"
              placeholder={t('admin.candidate.documentRequest.messagePlaceholder')}
              inputProps={{ rows: 3 }}
            />
            <span className="u-font-size-small secondary-text u-margin-top--small">
              {t('admin.candidate.documentRequest.messageDescription')}
            </span>
          </InputGroup>

          <div className="d-flex u-gap-medium flex-wrap">
            <InputGroup
              title={t('admin.candidate.documentRequest.reminderFrequencyDays')}
              className="u-flex-grow-1">
              <Input
                register={register}
                name="repeat_request_days"
                type="number"
                min={1}
                max={30}
              />
              <span className="u-font-size-small secondary-text u-margin-top--small">
                {t('admin.candidate.documentRequest.reminderFrequencyDescription')}
              </span>
            </InputGroup>
            <InputGroup
              title={t('admin.candidate.documentRequest.maxRequests')}
              className="u-flex-grow-1">
              <Input register={register} name="max_requests" type="number" min={1} max={50} />
              <span className="u-font-size-small secondary-text u-margin-top--small">
                {t('admin.candidate.documentRequest.maxRequestsDescription')}
              </span>
            </InputGroup>
          </div>

          {additionalCheckOptions.length > 0 && (
            <InputGroup title="Also applies to">
              <MultipleSelect
                value={additionalChecks}
                onChange={setAdditionalChecks}
                placeholder="Other checks this document covers"
                useSearch>
                {additionalCheckOptions.map(option => (
                  <MultipleSelect.Item
                    key={option.value}
                    value={option.value}
                    active={additionalChecks.includes(option.value)}>
                    {option.label}
                  </MultipleSelect.Item>
                ))}
              </MultipleSelect>
            </InputGroup>
          )}

          {submitError && (
            <FormMessage
              message={submitError}
              type={MessageType.Error}
              onClose={() => setSubmitError(null)}
              noMargin
            />
          )}

          <div className="d-flex u-gap-medium u-margin-top">
            <Button type="secondary" className="u-flex-grow-1" onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button type="primary" submit className="u-flex-grow-1" loading={isSubmitting}>
              {t('admin.candidate.documentRequest.sendRequest')}
            </Button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
};
